import { notEmpty } from "@product/scmp-sdk";
import { useAtomValue, useSetAtom } from "jotai";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import type { ParsedUrlQueryInput } from "querystring";
import { useCallback, useEffect, useMemo } from "react";

import { plusArticlePopupAtom } from "~/components/plus/article-popup/atoms";

import {
  decodePlusDetailedListingQuery,
  encodePlusDetailedListingQuery,
  removedPlusDetailedListingQuery,
} from "./navigation";
import type { PlusDetailedListingState } from "./types";

export const useActivePlusDetailedListing = () => {
  const searchParameters = useSearchParams();
  const activePlusDetailedListingStateInSearchParameters = useMemo(
    () => decodePlusDetailedListingQuery(searchParameters),
    [searchParameters],
  );

  const setPlusArticleState = useSetAtom(plusArticlePopupAtom);
  const plusArticleState = useAtomValue(plusArticlePopupAtom);
  useEffect(() => {
    setPlusArticleState(current => ({
      ...current,
      isShowDetailListingPopup: !!activePlusDetailedListingStateInSearchParameters,
    }));
  }, [activePlusDetailedListingStateInSearchParameters, setPlusArticleState]);

  const activePlusDetailedListingState = useMemo(
    () =>
      plusArticleState?.isShowDetailListingPopup &&
      activePlusDetailedListingStateInSearchParameters,
    [activePlusDetailedListingStateInSearchParameters, plusArticleState?.isShowDetailListingPopup],
  );

  return { activePlusDetailedListingState };
};

export const usePlusDetailedListingNavigation = () => {
  const router = useRouter();
  const setPlusArticleState = useSetAtom(plusArticlePopupAtom);

  const cleanQueryString = (input: ParsedUrlQueryInput) =>
    Object.fromEntries(Object.entries(input).filter(([_, value]) => notEmpty(value)));

  const closeActivePlusDetailedListing = useCallback(() => {
    setPlusArticleState(current => ({
      ...current,
      activeModal: undefined,
    }));

    void router.push(
      {
        pathname: router.pathname,
        query: cleanQueryString(removedPlusDetailedListingQuery(router.query)),
      },
      undefined,
      { shallow: true },
    );
  }, [router, setPlusArticleState]);

  const openActivePlusDetailedListing = useCallback(
    (plusDetailedListingState: PlusDetailedListingState, activeModal?: "listing") => {
      if (activeModal === "listing") {
        closeActivePlusDetailedListing();
      }

      setTimeout(() => {
        const plusDetailedListingQuery = encodePlusDetailedListingQuery(plusDetailedListingState);
        setPlusArticleState(current => ({
          ...current,
          activeModal,
        }));
        void router.push(
          {
            pathname: router.pathname,
            query: cleanQueryString({
              ...router.query,
              ...plusDetailedListingQuery,
            }),
          },
          undefined,
          { shallow: true },
        );
      }, 500);
    },
    [router, setPlusArticleState, closeActivePlusDetailedListing],
  );

  return { closeActivePlusDetailedListing, openActivePlusDetailedListing };
};
